import type { PeerInfo } from '../../server/protocol';

export interface ParticipantsActions {
  onFollowToggle(clientId: string): void;
  onInvite(clientId: string): void;
  onInviteAll(): void;
}

/** So viele Avatare direkt sichtbar, der Rest steckt hinter „+N". */
const MAX_VISIBLE = 3;

/**
 * Immer sichtbare Teilnehmer-Leiste in der Kopfzeile: Avatare der Online-
 * Teilnehmer, Antippen folgt der Person. Bei mehreren Teilnehmern klappt
 * „+N" eine Liste mit Folgen-/Einladen-Buttons auf.
 */
export class ParticipantsBar {
  el: HTMLDivElement;
  private strip: HTMLDivElement;
  private moreBtn: HTMLButtonElement;
  private menu: HTMLDivElement;
  private peers: PeerInfo[] = [];
  private youId: string | null = null;
  private followingId: string | null = null;
  private invited = new Set<string>();
  private menuOpen = false;
  private outsideHandler = (e: MouseEvent): void => {
    if (!this.el.contains(e.target as Node)) this.closeMenu();
  };
  private keyHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.closeMenu();
  };

  constructor(root: HTMLElement, private actions: ParticipantsActions) {
    this.el = document.createElement('div');
    this.el.className = 'participants hidden';

    this.strip = document.createElement('div');
    this.strip.className = 'participants-strip';

    this.moreBtn = document.createElement('button');
    this.moreBtn.type = 'button';
    this.moreBtn.className = 'peer-avatar peer-more';
    this.moreBtn.title = 'Teilnehmer — folgen & einladen';
    this.moreBtn.onclick = () => this.toggleMenu();

    this.menu = document.createElement('div');
    this.menu.className = 'participants-menu hidden';

    this.el.append(this.strip, this.menu);
    root.appendChild(this.el);
  }

  setPeers(peers: PeerInfo[], you: PeerInfo | null, followingId: string | null = null): void {
    this.peers = peers;
    this.youId = you?.clientId ?? null;
    this.followingId = followingId;
    this.invited = new Set([...this.invited].filter((id) => peers.some((p) => p.clientId === id)));
    this.el.classList.toggle('hidden', peers.length === 0);
    if (peers.length === 0) this.closeMenu();
    this.render();
  }

  /** Einladung wurde an die Person geschickt — Knopf spiegelt das wider. */
  markInvited(clientId: string): void {
    this.invited.add(clientId);
    this.render();
  }

  closeMenu(): void {
    if (!this.menuOpen) return;
    this.menuOpen = false;
    this.menu.classList.add('hidden');
    document.removeEventListener('click', this.outsideHandler, true);
    document.removeEventListener('keydown', this.keyHandler, true);
  }

  private toggleMenu(): void {
    if (this.menuOpen) {
      this.closeMenu();
      return;
    }
    this.menuOpen = true;
    this.renderMenu();
    this.menu.classList.remove('hidden');
    document.addEventListener('click', this.outsideHandler, true);
    document.addEventListener('keydown', this.keyHandler, true);
  }

  private render(): void {
    const others = this.peers.filter((p) => p.clientId !== this.youId);
    this.strip.replaceChildren();
    for (const p of others.slice(0, MAX_VISIBLE)) this.strip.appendChild(this.avatarBtn(p));
    // Chip immer zeigen: „+N" bei weiteren Teilnehmern, sonst „…" — er öffnet die
    // Liste mit Folgen-/Einladen-Buttons (bei wenigen Teilnehmern der einzige
    // Einladungs-Einstieg neben dem Teilen-Dialog).
    const rest = Math.max(0, others.length - MAX_VISIBLE);
    this.moreBtn.textContent = rest > 0 ? `+${rest}` : '⋯';
    this.strip.appendChild(this.moreBtn);
    if (this.menuOpen) this.renderMenu();
  }

  private avatarBtn(p: PeerInfo): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'peer-avatar';
    if (this.followingId === p.clientId) b.classList.add('is-following');
    b.style.setProperty('--peer-color', p.color);
    b.textContent = p.name.charAt(0).toUpperCase();
    b.title = `„${p.name}" — Ansicht folgen/beenden`;
    b.onclick = () => this.actions.onFollowToggle(p.clientId);
    return b;
  }

  private renderMenu(): void {
    const others = this.peers.filter((p) => p.clientId !== this.youId);
    this.menu.replaceChildren();

    const head = document.createElement('div');
    head.className = 'participants-menu-head';
    const title = document.createElement('span');
    title.textContent = `Teilnehmer (${this.peers.length})`;
    const inviteAllBtn = document.createElement('button');
    inviteAllBtn.type = 'button';
    inviteAllBtn.className = 'top-btn';
    inviteAllBtn.textContent = 'Alle einladen';
    inviteAllBtn.title = 'Alle auffordern, deiner Ansicht zu folgen';
    inviteAllBtn.disabled = others.length === 0;
    inviteAllBtn.onclick = () => {
      this.actions.onInviteAll();
      this.closeMenu();
    };
    head.append(title, inviteAllBtn);
    this.menu.appendChild(head);

    for (const p of this.peers) {
      const row = document.createElement('div');
      row.className = 'participants-row';
      const isYou = p.clientId === this.youId;

      const dot = document.createElement('span');
      dot.className = 'peer-avatar peer-avatar-static';
      dot.style.setProperty('--peer-color', p.color);
      dot.textContent = p.name.charAt(0).toUpperCase();

      const name = document.createElement('span');
      name.className = 'participants-name';
      name.textContent = isYou ? `${p.name} (du)` : p.name;

      row.append(dot, name);
      if (!isYou) {
        const followBtn = document.createElement('button');
        followBtn.type = 'button';
        const isFollowing = this.followingId === p.clientId;
        followBtn.className = isFollowing ? 'follow-btn is-following' : 'follow-btn';
        followBtn.textContent = isFollowing ? 'Folgt ✓' : 'Folgen';
        followBtn.title = isFollowing
          ? 'Ansicht wieder selbst steuern'
          : `Ansicht von „${p.name}" spiegeln`;
        followBtn.onclick = () => this.actions.onFollowToggle(p.clientId);

        const inviteBtn = document.createElement('button');
        inviteBtn.type = 'button';
        inviteBtn.className = 'top-btn invite-btn';
        const wasInvited = this.invited.has(p.clientId);
        inviteBtn.textContent = wasInvited ? 'Eingeladen ✓' : 'Einladen';
        inviteBtn.title = wasInvited
          ? 'Einladung wurde gesendet'
          : `„${p.name}" bitten, deiner Ansicht zu folgen`;
        inviteBtn.disabled = wasInvited;
        inviteBtn.onclick = () => {
          this.actions.onInvite(p.clientId);
          this.closeMenu();
        };

        row.append(followBtn, inviteBtn);
      }
      this.menu.appendChild(row);
    }
  }
}
